import {
  ActivatedMobileUserMinimumData,
  InviteUserBody,
  MobileUser,
  MobileUserCreateBody,
  MobileUserUpdateBody,
  ModificationLog,
} from '../types';
import type { SortByNameOrder } from '../utils/sortByName';
import { client } from './index';
import { withSettleWatchdog } from './settleWatchdog';

interface PaginationInterface {
  page?: number;
  search?: string;
  sortOrder?: SortByNameOrder;
}

/**
 * DS-3967. The unsorted state emits nothing, which keeps that request byte for
 * byte the one the client sends today. Anything else would make the default
 * request a new one to the service, and AC-1 is stated against the current one.
 */
const SORT_PARAM_BY_ORDER: Record<SortByNameOrder, string | undefined> = {
  unsorted: undefined,
  asc: 'name_asc',
  desc: 'name_desc',
};

interface FetchUsersInterface {
  page: number;
  total_pages: number;
  total_users: number;
  users: MobileUser[];
}
interface FetchLogsInterface {
  page: number;
  total_pages: number;
  total_users: number;
  users: ModificationLog[];
}

interface UserInfo {
  mobile_user: {
    id: number;
    name: string;
    last_name: string;
    code: string;
    email: string;
    phone: string;
    identifier: string;
    answers: number;
    chat: number;
    tasks: number;
    assigned: number;
    evaluate: number;
    metadata: number;
    modify: number;
    edit_forms: number;
    evaluate_delete: number;
    users: number;
    export: number;
  };
  group: { id: number[] };
  location: { id: number[] };
  nestable_locations: number[];
  form: { id: number[] };
  signature: {
    full_name: string;
    personal_email: string;
    rut: string;
    country: string;
  };
  pending_signature_forms: { id: number[] };
  pending_signature_groups: { id: number[] };
}

async function getActivatedMobileUsers({
  page = 1,
  search,
  sortOrder = 'unsorted',
}: PaginationInterface) {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  // Left unconditional on purpose: this endpoint emits `search=undefined` today
  // when there is no term, and fixing that here would move the request TC-15
  // pins for a reason unrelated to the order.
  params.append('search', search);
  const sort = SORT_PARAM_BY_ORDER[sortOrder];
  if (sort) params.append('sort', sort);

  const response = await client.get<FetchUsersInterface>(
    `/mobile_users_react/activated?${params.toString()}`,
  );
  const { data } = response;
  return data;
}

async function getAllActivatedMobileUsers() {
  const response = await client.get<ActivatedMobileUserMinimumData[]>('/mobile_users_react/all');
  const { data } = response;
  return data;
}

// DS-4034. The deactivated tab now has the same search box as the active one,
// so this request carries the term too. Unlike the activated one it only emits
// `search` when there is something to search for: nothing pins this request's
// current shape, so there is no reason to reproduce the `search=undefined` of
// its neighbour.
async function getDeactivatedMobileUsers({
  page = 1,
  search,
  sortOrder = 'unsorted',
}: PaginationInterface) {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  if (search) params.append('search', search);
  const sort = SORT_PARAM_BY_ORDER[sortOrder];
  if (sort) params.append('sort', sort);

  const response = await client.get<FetchUsersInterface>(
    `/mobile_users_react/deactivated?${params.toString()}`,
  );
  const { data } = response;
  return data;
}

// Review finding C. The server builds the whole .xlsx and uploads it to S3 before
// responding, so this request's duration is unbounded and scales with account size --
// unlike Tickets' access checks, `withSettleWatchdog`'s default 15s can't be trusted
// as this request's deadline on its own. Pairing an explicit axios timeout (same
// pattern as `findingsApi`'s Tickets calls) restores a real timeout-under-watchdog
// relationship: the axios timeout is the request's actual deadline, and the watchdog,
// set strictly longer, is back to guarding only its narrow original case (an
// interceptor response that never settles).
const EXPORT_TIMEOUT_MS = 120_000;
const EXPORT_WATCHDOG_MS = 130_000;
const EXPORT_WATCHDOG_MESSAGE = 'Mobile users export request never settled';

// DS-4034. One file per tab: the active tab exports the active users, the
// deactivated one the deactivated users (with their deactivation date). The
// search term is deliberately not sent: the export is always the whole tab.
//
// Phase 13 review, finding 5. Wrapped in the same `withSettleWatchdog` guard
// as `findingsApi`'s Tickets calls: without it, a `password_change_required`
// response mid-export would leave the export button's `isExporting` state
// stuck loading forever, since the interceptor's promise never settles and
// neither `.then`, `.catch`, nor `.finally` in `MobileUsersExportButton` would
// ever run.
async function exportMobileUsers(scope: 'activated' | 'deactivated') {
  const response = await withSettleWatchdog(
    client.get<{
      status: string;
      download_url?: string;
      filename?: string;
      error?: string;
    }>(`/mobile_users_react/export?scope=${scope}`, { timeout: EXPORT_TIMEOUT_MS }),
    EXPORT_WATCHDOG_MESSAGE,
    EXPORT_WATCHDOG_MS,
  );
  return response.data;
}

async function getMobileUsersHistory({ page = 1 }: PaginationInterface) {
  const response = await client.get<FetchLogsInterface>(`/mobile_users_react/history?page=${page}`);
  const { data } = response;
  return data;
}

// The delete function deactivates a user
export interface RemovalImpact {
  cascades: boolean;
  other_accounts: number;
  display_email: string | null;
}

/**
 * How much access this deactivation actually revokes.
 *
 * Removing someone from a secondary account leaves the rest untouched. Removing
 * them from the account where their login lives locks them out of all of them,
 * and they cannot undo it themselves. From this screen both look identical, so
 * the dialog asks first.
 */
async function getRemovalImpact(mobileUserId: number) {
  const response = await client.get<RemovalImpact>(
    `/mobile_users_react/removal_impact/${mobileUserId}`,
  );
  return response.data;
}

export interface BulkRemovalImpact {
  cascading: (RemovalImpact & { mobile_user_id: number })[];
  /** How many of the selected users the backend actually looked at. */
  checked: number;
  requested: number;
}

/**
 * The same question for a whole selection, in one request.
 *
 * The bulk action is the higher-volume route and the one nobody reviews row by
 * row, so the warning has to reach it too. Only the users who do cascade come
 * back: the rest would be a payload of zeroes.
 */
async function getBulkRemovalImpact(mobileUserIds: number[]) {
  const response = await client.post<BulkRemovalImpact>(
    '/mobile_users_react/bulk_removal_impact',
    { mobile_user_ids: mobileUserIds },
  );
  return response.data;
}

async function deleteMobileUser(mobileUserId: number) {
  const response = await client.delete<MobileUser>(`/mobile_users_react/delete/${mobileUserId}`);
  const { data } = response;
  return data;
}

async function bulkDeactivateMobileUsers(mobileUserIds: number[]) {
  const response = await client.delete<MobileUser>('/mobile_users_react/bulk_delete', {
    data: { mobile_user_ids: mobileUserIds },
  });
  const { data } = response;
  return data;
}

async function updateMobileUser(body: MobileUserUpdateBody) {
  const response = await client.put<MobileUser>(
    `mobile_users_react/update/${body.mobile_user.id}`,
    body,
  );
  const { data } = response;
  return data;
}

// TODO: Find how to combine this function with the previous one, since they have the same purpose but the typing is different.
async function editMobileUser(updateMobileUserParams: MobileUserCreateBody, mobileUserId: string) {
  const response = await client.put<MobileUser>(
    `mobile_users_react/update/${mobileUserId}`,
    updateMobileUserParams,
  );
  const { data } = response;
  return data;
}

// You use the update function to edit and to activate users
async function reactivateMobileUser(mobileUserId: number) {
  const response = await client.put<MobileUser>(`mobile_users_react/update/${mobileUserId}`, {
    mobile_user: {
      reactivate_user: 'true',
    },
    location: { id: [''] },
    form: { id: [''] },
  });
  const { data } = response;
  return data;
}

async function createMobileUser(body: MobileUserCreateBody) {
  const response = await client.post<MobileUser>('mobile_users_react/create', body);
  const { data } = response;
  return data;
}

async function validateNewUserEmail(email: string) {
  try {
    const response = await client.get<{ email_exists: boolean }>('/validate_params', {
      params: { email },
    });
    return response.data;
  } catch (error) {
    console.log('There was an error validating the new email', error);
  }
}

async function inviteUser(body: InviteUserBody) {
  const response = await client.post<{ error?: string; message?: string }>(
    'mobile_users_react/invite',
    body,
  );
  const { data } = response;
  return data;
}

async function reInviteUser(mobileUserId: string) {
  const response = await client.post<{ error?: string; message?: string }>(
    '/mobile_users_react/re_invite',
    {
      user_id: mobileUserId,
    },
  );
  const { data } = response;
  return data;
}

async function getUserInfo(mobileUserId: string) {
  const response = await client.get<UserInfo>(`/mobile_users_react/${mobileUserId}`);
  const { data } = response;
  return data;
}

async function exportTemplate() {
  const response = await client.get<{
    status: string;
    download_url?: string;
    filename?: string;
    error?: string;
  }>('/mobile_users/export_template');
  return response.data;
}

export interface ImportPreview {
  total: number;
  linked_emails: string[];
  linked_count: number;
  /** Addresses already taken that the import will NOT create. */
  blocked_emails: string[];
  blocked_count: number;
  new_count: number;
}

/**
 * Which of the addresses in the file already belong to a DataScope person. The
 * server parses the sheet with the same code the import uses, so the notice
 * cannot describe rows the import would classify differently.
 */
async function importPreview(file: File) {
  const formData = new FormData();
  formData.append('file', file);
  const response = await client.post<ImportPreview>('/mobile_users/import_preview', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

async function importTemplate(file: File) {
  const formData = new FormData();
  formData.append('file', file);
  const response = await client.post<{ status: string; message?: string; error?: string }>(
    '/mobile_users/import_template',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
}

export const mobileUsersApi = {
  getActivatedMobileUsers,
  getAllActivatedMobileUsers,
  getDeactivatedMobileUsers,
  getMobileUsersHistory,
  deleteMobileUser,
  getRemovalImpact,
  getBulkRemovalImpact,
  bulkDeactivateMobileUsers,
  updateMobileUser,
  reactivateMobileUser,
  createMobileUser,
  validateNewUserEmail,
  inviteUser,
  reInviteUser,
  getUserInfo,
  editMobileUser,
  exportTemplate,
  importPreview,
  importTemplate,
  exportMobileUsers,
};
